import React, { useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useMutation } from "@apollo/client/react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import AddEventForm from "../components/AddEventForm";
import AllEventsTable from "../components/AllEventsTable";
import UserEventCardView from "../components/UserEventCardView";
import themestyle from "../theme";
import ConfirmDialog from "../components/confirmdialogbox";
import CustomSnackbar from "../components/Snackbar";
import { DELETE_EVENT } from "../graphql/mutation";
import { useQuery } from "@apollo/client";
import { ME } from "../graphql/query";
import type { DeleteEventMutationResponse } from "../interfaces/event.interface";

type ViewType = "ALL_EVENTS" | "ADD_EVENT" | "VIEW_EVENT" | "EDIT_EVENT";

const EventManagement: React.FC = () => {
  const navigate = useNavigate();
  const { mode, id } = useParams<{ mode?: string; id?: string }>();
  const location = useLocation();

  const [refreshCount, setRefreshCount] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [eventToDelete, setEventToDelete] = useState<{ id: string; name: string } | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarType, setSnackbarType] = useState<"success" | "error" | "info">("success");

  const [deleteEvent] = useMutation<DeleteEventMutationResponse, { id: string }>(DELETE_EVENT);

  const getActiveView = (): ViewType => {
    if (location.pathname === "/events/add") return "ADD_EVENT";
    if (mode === "view" && id) return "VIEW_EVENT";
    if (mode === "edit" && id) return "EDIT_EVENT";
    return "ALL_EVENTS";
  };

  const activeView = getActiveView();
  const selectedEventId = id;

  const { data: userData } = useQuery(ME, {
    fetchPolicy: "cache-first",
  });

  const storedUser = (() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "null");
    } catch {
      return null;
    }
  })();
  const userRole = userData?.me?.role?.toUpperCase() || storedUser?.role?.toUpperCase() || "";

  useEffect(() => {
    document.title = "Events - United Grooves";
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const paymentStatus = params.get("paymentStatus");
    const paymentMessage = params.get("paymentMessage");
    if (paymentStatus && paymentMessage) {
      setSnackbarMessage(paymentMessage);
      setSnackbarType(paymentStatus === "success" ? "success" : "error");
      setSnackbarOpen(true);
      if (paymentStatus === "success") {
        setRefreshCount((prev) => prev + 1);
      }
      navigate("/events", { replace: true });
    }
  }, [location.search, navigate]);

  const handleView = (eventId: string) => {
    navigate(`/events/view/${eventId}`, { state: { from: location.pathname } });
  };

  const handleReport = (eventId: string) => {
    navigate(`/events/report/${eventId}`);
  };

  const handleEdit = (eventId?: string) => {
    const targetId = eventId || selectedEventId;
    if (targetId) navigate(`/events/edit/${targetId}`, { state: { from: location.pathname } });
  };

  const handleBack = () => {
    const from = (location.state as any)?.from;
    navigate(from || "/events");
  };

  const handleSuccess = () => {
    navigate("/events");
    setRefreshCount((prev) => prev + 1);
  };

  const handleDeleteClick = (eventId: string, name: string) => {
    setEventToDelete({ id: eventId, name });
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!eventToDelete) return;
    try {
      const { data } = await deleteEvent({ variables: { id: eventToDelete.id } });
      if (data?.deleteEvent?.success) {
        setSnackbarMessage(data.deleteEvent.message || "Event deleted successfully");
        setSnackbarType("error");
        setSnackbarOpen(true);
        handleSuccess();
      } else {
        setSnackbarMessage(data?.deleteEvent?.message || "Failed to delete event");
        setSnackbarType("error");
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setDeleteDialogOpen(false);
      setEventToDelete(null);
    }
  };

  const isAllEventsActive = activeView === "ALL_EVENTS";

  return (
    <Box sx={{ mt: { xs: 1, sm: 3, md: 3 }, px: { xs: 1, md: 3 } }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", md: "center" },
          gap: { xs: 2, md: 0 },
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 0.5 }}>
          {(activeView === "VIEW_EVENT" || activeView === "EDIT_EVENT") && (
            <Box
              onClick={handleBack}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
                cursor: "pointer",
                color: "#6B7280",
                fontSize: "14px",
                fontWeight: 500,
                mb: 0.5,
              }}
            >
              <ArrowBackIosNewIcon sx={{ fontSize: "14px" }} />
              Back
            </Box>
          )}

          <Typography variant="h5" fontWeight={600} sx={{ fontSize: "24px" }} fontFamily={themestyle.fontFamily.Roboto}>
            {activeView === "VIEW_EVENT" ? "View Event" : activeView === "EDIT_EVENT" ? "Edit Event" : "Events"}
          </Typography>
        </Box>

        {userRole !== "USER" && (activeView === "ALL_EVENTS" || activeView === "ADD_EVENT") && (
          <Box
            sx={{
              display: "flex",
              backgroundColor: themestyle.colors.white,
              borderRadius: "6px",
              padding: "6px",
              boxShadow: "0px 4px 12px rgba(0,0,0,0.05)",
              width: { xs: "100%", md: "fit-content" },
              justifyContent: { xs: "space-between", md: "flex-start" },
            }}
          >
            <Box
              onClick={() => navigate("/events")}
              sx={{
                cursor: "pointer",
                flex: 1,
                textAlign: "center",
                px: { xs: 2, sm: 4 },
                py: { xs: 1.5, sm: 1.5 },
                fontSize: { xs: "14px", sm: "16px" },
                borderRadius: "6px",
                fontWeight: 600,
                whiteSpace: "nowrap",
                color: isAllEventsActive ? "#4CAF50" : "#6B7280",
                backgroundColor: isAllEventsActive ? "#EEF7E9" : "transparent",
                transition: "all 0.3s ease",
              }}
            >
              All Events
            </Box>
            <Box
              onClick={() => navigate("/events/add")}
              sx={{
                cursor: "pointer",
                flex: 1,
                textAlign: "center",
                px: { xs: 2, sm: 4 },
                py: { xs: 1.5, sm: 1.5 },
                fontSize: { xs: "14px", sm: "16px" },
                borderRadius: "6px",
                fontWeight: 600,
                whiteSpace: "nowrap",
                color: activeView === "ADD_EVENT" ? "#4CAF50" : "#6B7280",
                backgroundColor: activeView === "ADD_EVENT" ? "#EEF7E9" : "transparent",
                transition: "all 0.3s ease",
              }}
            >
              Add Events
            </Box>
          </Box>
        )}
      </Box>

      {activeView === "ALL_EVENTS" && (
        userRole === "USER" ? (
          <UserEventCardView onView={handleView} refreshTrigger={refreshCount} />
        ) : (
          <AllEventsTable
            refreshTrigger={refreshCount}
            onView={handleView}
            onDelete={handleDeleteClick}
            onReport={handleReport}
          />
        )
      )}

      {activeView === "ADD_EVENT" && <AddEventForm mode="add" onSuccess={handleSuccess} onCancel={handleBack} />}

      {activeView === "VIEW_EVENT" && (
        <AddEventForm mode="view" id={selectedEventId} onCancel={handleBack} onEdit={() => handleEdit(selectedEventId)} />
      )}

      {activeView === "EDIT_EVENT" && (
        <AddEventForm mode="edit" id={selectedEventId} onSuccess={handleSuccess} onCancel={() => navigate(`/events/view/${selectedEventId}`)} />
      )}

      <ConfirmDialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setEventToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        message={`Are you sure you want to delete ${eventToDelete?.name || "this event"}?`}
        icon={<DeleteOutlineIcon sx={{ color: "#EB2857", fontSize: 36 }} />}
        iconBgColor="danger"
        confirmButtonColor="danger"
        confirmText="Delete"
        cancelText="Cancel"
      />

      <CustomSnackbar open={snackbarOpen} message={snackbarMessage} type={snackbarType} onClose={() => setSnackbarOpen(false)} />
    </Box>
  );
};

export default EventManagement;
